import { TZDate } from '@date-fns/tz';
import { addMinutes, format, formatISO } from 'date-fns';

const LOCAL_DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * A venue's local date and time (`YYYY-MM-DDTHH:mm[:ss]`, as experiences and events
 * store it — never converted) as an ISO date-time carrying the venue's UTC offset on
 * that date: `2026-11-09T18:00:00-08:00`, `2026-07-04T18:00:00-07:00` (DST-aware).
 * `plusMinutes` shifts it by elapsed time first (an end time from a duration).
 *
 * Without a known time zone the offset is omitted (`2026-11-09T18:00:00`) rather than
 * guessed. Returns null for a malformed input.
 */
const toZonedIsoDateTime = (
  localDateTime: string,
  timeZone?: string | null,
  plusMinutes = 0,
): string | null => {
  const match = localDateTime.match(LOCAL_DATE_TIME);
  if (!match) return null;
  const [year, month, day, hour, minute, second = 0] = match
    .slice(1)
    .map((part) => Number(part ?? 0));

  if (timeZone) {
    const zoned = new TZDate(
      year,
      month - 1,
      day,
      hour,
      minute,
      second,
      timeZone,
    );
    // An unknown zone yields an invalid date rather than throwing.
    if (!Number.isNaN(zoned.getTime())) {
      return formatISO(addMinutes(zoned, plusMinutes));
    }
  }

  // No usable zone: keep the wall-clock time, without an offset. UTC is used only as a
  // fixed frame for the arithmetic, so no local DST shift can creep in.
  const wallClock = new TZDate(
    year,
    month - 1,
    day,
    hour,
    minute,
    second,
    'UTC',
  );
  return format(addMinutes(wallClock, plusMinutes), "yyyy-MM-dd'T'HH:mm:ss");
};

export default toZonedIsoDateTime;
